import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Stack,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/router";
import { useRecoilValue } from "recoil";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useGomakeAxios } from "@/hooks";
import { PrimaryButton } from "@/components/button/primary-button";
import { useTicketChatbot } from "@/components/ticket-chatbot";
import {
  addTicketMessage,
  createTicket,
} from "@/services/api-service/tickets/tickets-api";
import { printHouseProfile } from "@/store/print-house-profile";
import { userProfileState } from "@/store/user-profile";
import { useStyle } from "../trial-expired-modal/style";

interface AiCreditsRequiredModalProps {
  open: boolean;
  /** When true, modal cannot be dismissed — page stays locked */
  locked?: boolean;
  onClose?: () => void;
  /** Optional server message from credit gate */
  message?: string | null;
  /** true when print house never received credits */
  neverLoaded?: boolean;
  /** true when AI is disabled for the print house */
  disabled?: boolean;
}

export const AiCreditsRequiredModal = ({
  open,
  locked = false,
  onClose,
  neverLoaded,
  disabled = false,
}: AiCreditsRequiredModalProps) => {
  const { t, i18n } = useTranslation();
  const dir = (i18n.dir?.() || "ltr") as "rtl" | "ltr";
  const { classes } = useStyle();
  const { callApi } = useGomakeAxios();
  const { openChatbot, navigateToTicket, openNewTicket } = useTicketChatbot();
  const printHouse = useRecoilValue(printHouseProfile);
  const userProfile = useRecoilValue(userProfileState);
  const router = useRouter();
  const onGoAiPage = router.pathname === "/gomake-ai";
  const [contactLoading, setContactLoading] = useState(false);

  const title = disabled
    ? String(t("aiCredits.disabledTitle"))
    : neverLoaded
      ? String(t("aiCredits.noCreditsTitle"))
      : String(t("aiCredits.exhaustedTitle"));
  const body = disabled
    ? String(t("aiCredits.disabledBody"))
    : neverLoaded
      ? String(t("aiCredits.noCreditsActivateBody"))
      : String(t("aiCredits.exhaustedBody"));

  const buttonSx = {
    width: "100% !important",
    minWidth: "0 !important",
    height: "48px !important",
    minHeight: "48px",
    boxSizing: "border-box" as const,
    fontSize: 14,
    fontWeight: 600,
    borderRadius: "8px",
    textTransform: "none" as const,
    px: 2,
  };

  const handleDialogClose = (
    _event: object,
    reason?: "backdropClick" | "escapeKeyDown"
  ) => {
    if (onGoAiPage || locked) return;
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    onClose?.();
  };

  const handleCloseClick = () => {
    if (onGoAiPage) return;
    onClose?.();
  };

  const handleContactUs = async () => {
    try {
      setContactLoading(true);
      const username =
        userProfile?.firstName && userProfile?.lastName
          ? `${userProfile.firstName} ${userProfile.lastName}`
          : userProfile?.email || "Unknown";
      const ticketTitle = String(t("aiCredits.needMoreTitle"));
      const ticketMessage = String(t("aiCredits.contactUsHint"));

      const ticket = await createTicket(callApi, {
        title: ticketTitle,
        priority: "medium",
        printHouseId: printHouse?.id || "",
        printHouseName: printHouse?.name || "",
        createdByUserId: userProfile?.userId || userProfile?.id || "",
        username,
        email: userProfile?.email || "",
      });

      // Keep Go-AI locked; only open support ticket on top
      if (!ticket?.id) {
        openNewTicket();
        return;
      }

      await addTicketMessage(
        callApi,
        ticket.id,
        ticketMessage,
        "user",
        username
      );
      openChatbot();
      navigateToTicket(ticket.id);
    } catch (error) {
      console.error("AI Credits Contact Us failed:", error);
      openNewTicket();
    } finally {
      setContactLoading(false);
    }
  };

  const handleViewDashboard = () => {
    router.push("/gomake-ai/dashboard");
  };

  const handleBackToHome = () => {
    router.push("/home");
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={onGoAiPage || locked}
      maxWidth="sm"
      fullWidth
      dir={dir}
      hideBackdrop={false}
      PaperProps={{
        sx: {
          position: "relative",
          borderRadius: "16px",
          background: "linear-gradient(135deg, #ffffff 0%, #fef5fa 100%)",
          margin: { xs: "16px", sm: "32px" },
          pointerEvents: "auto",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            pointerEvents: "auto",
          },
        },
      }}
      sx={{
        pointerEvents: "auto",
        zIndex: (theme) => theme.zIndex.modal + 2,
      }}
    >
      <DialogContent sx={{ padding: { xs: "24px 20px", sm: "40px" }, position: "relative" }}>
        {!onGoAiPage && (
          <IconButton
            onClick={handleCloseClick}
            aria-label={String(t("modal.close"))}
            sx={{
              position: "absolute",
              top: 8,
              insetInlineEnd: 8,
              color: "#6b7280",
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
        <Stack spacing={3} alignItems="center">
          <Box sx={classes.iconContainer}>
            <Box sx={classes.icon}>
              <AccountBalanceWalletOutlinedIcon
                sx={{ fontSize: { xs: 26, sm: 36 }, color: "#9333ea" }}
              />
            </Box>
          </Box>

          <Box sx={classes.titleContainer}>
            <Box sx={classes.title}>{title}</Box>
            <Box sx={classes.subtitle}>{body}</Box>
          </Box>

          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            width="100%"
            sx={{ gap: "24px" }}
          >
            {onGoAiPage && (
              <Box sx={{ flex: "1 1 0", minWidth: 0, maxWidth: 180 }}>
                <PrimaryButton
                  variant="outlined"
                  onClick={handleBackToHome}
                  disabled={contactLoading}
                  fullWidth
                  sx={{
                    ...buttonSx,
                    borderColor: "#e5e7eb",
                    color: "#6b7280",
                  }}
                >
                  {String(t("aiCredits.backToHome"))}
                </PrimaryButton>
              </Box>
            )}
            <Box sx={{ flex: "1 1 0", minWidth: 0, maxWidth: 180 }}>
              <PrimaryButton
                variant="outlined"
                onClick={handleViewDashboard}
                disabled={contactLoading}
                fullWidth
                sx={{
                  ...buttonSx,
                  borderColor: "#e5e7eb",
                  color: "#6b7280",
                }}
              >
                {String(t("aiCredits.dashboardTitle"))}
              </PrimaryButton>
            </Box>
            <Box sx={{ flex: "1 1 0", minWidth: 0, maxWidth: 180 }}>
              <PrimaryButton
                variant="contained"
                onClick={handleContactUs}
                disabled={contactLoading}
                fullWidth
                sx={buttonSx}
              >
                {contactLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                    <span>{String(t("aiCredits.contactUs"))}</span>
                  </Box>
                ) : (
                  String(t("aiCredits.contactUs"))
                )}
              </PrimaryButton>
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
